import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { injectIntl, intlShape } from "@ai360/core/intl";
import { sortBy } from "lodash";
import {
    AgBytesAPI,
    AgEventAPI,
    Ai360API,
    CropAPI,
    EventAPI,
    FieldAPI,
    ISelectOption,
    RecAPI,
} from "@ai360/core";

import { keywords } from "~/action-panel/components/common/planting/keywords";
import * as blending from "~/action-panel/components/common/product-blend-modal/blending-utils";
import { DialogBox, DialogBoxFooterType, Loader, SelectInput } from "~/core";
import { fetchDropdownData } from "~/core/dropdowns/actions";
import { actions as picklistActions, selectors as picklistSelectors } from "~/core/picklist";
import {
    getPickListCode,
    PICKLIST_CROPPING_SEASON,
    PICKLIST_CROP_PURPOSE,
} from "~/core/picklist/picklist-names";
import { getTheUserGuid } from "~/login";
import { models, actions as recEventsActions } from "~/recs-events";
import { batchEditEventsSave } from "~/recs-events/events/actions";
import { getEventGeneralGuidToEventSummaryMap } from "~/recs-events/events/selectors";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { logFirebaseEvent } from "~/utils/firebase";
import {
    filterLinkedProducts,
    isProductUnlinked,
} from "~/admin/setup/customer/agvance-linked-products";
import { useLinkedProductRestriction } from "~/admin/setup/customer/use-linked-product-restriction";
import { messages as globalMessages } from "~/i18n-messages";

import "./batch-edit-events.css";
import { getSelectedEventGuidSet } from "../../selectors";
import { toggleBatchEditEventsModal } from "../../actions";
import { getCropDropdownState } from "../../../event-info/selectors";
import { fetchedDropdownData } from "../../../event-info/actions";
import { messages } from "../../../i18n-messages";
import WithNewApiResult from "~/hocs/with-api/with-new-api-result";

const MAX_BATCH_EDIT_EVENTS = 500;

const ProductMixTypeOptions = [
    {
        value: EventAPI.BatchEditProductType.SingleProduct,
        label: "Single Product",
    },
    {
        value: EventAPI.BatchEditProductType.CustomBlend,
        label: "Custom Blend",
    },
    {
        value: EventAPI.BatchEditProductType.ChemicalProductMix,
        label: "Chemical Product Mix",
    },
    {
        value: EventAPI.BatchEditProductType.ManureProduct,
        label: "Manure Product",
    },
];

const FilterAttributeMap = {
    [EventAPI.BatchEditFilterAttributeName.MonitorCrop]: "Monitor Crop",
    [EventAPI.BatchEditFilterAttributeName.MonitorProduct]: "Monitor Product",
    [EventAPI.BatchEditFilterAttributeName.MonitorVariety]: "Monitor Variety",
    [EventAPI.BatchEditFilterAttributeName.Product]: "Product",
    [EventAPI.BatchEditFilterAttributeName.VarietyHybrid]: "Variety/Hybrid",
};

const AttributeToUpdateMap = {
    [EventAPI.BatchEditAttributeToUpdate.Crop]: "Crop",
    [EventAPI.BatchEditAttributeToUpdate.ProductMix]: "Product Mix",
    [EventAPI.BatchEditAttributeToUpdate.Season]: "Season",
    [EventAPI.BatchEditAttributeToUpdate.VarietyHybrid]: "Variety/Hybrid",
};

type BatchEditEventsModalProps = {
    intl: intlShape;
};

const BatchEditEventsModal = ({
    intl: { formatMessage },
}: BatchEditEventsModalProps): ReactElement => {
    const dispatch = useAppDispatch();
    const selectedEventGuidSet = useAppSelector(getSelectedEventGuidSet);
    const userGuid = useAppSelector(getTheUserGuid);
    const agEventSummaryMap = useAppSelector(getEventGeneralGuidToEventSummaryMap);
    const cropData = useAppSelector(getCropDropdownState);
    const cropPurposeData = useAppSelector(
        (state) =>
            picklistSelectors.getPicklistOptionsFromCode(
                state,
                getPickListCode(PICKLIST_CROP_PURPOSE)
            ) as ISelectOption<string>[]
    );
    const seasons = useAppSelector(
        (state) =>
            picklistSelectors.getPicklistOptionsFromCode(
                state,
                getPickListCode(PICKLIST_CROPPING_SEASON)
            ) as FieldAPI.ISeason[]
    );

    const closeModal = () => dispatch(toggleBatchEditEventsModal(false));

    // State for top-level attribute and optional data filter inputs
    const [attributeToUpdate, setAttributeToUpdate] =
        useState<EventAPI.BatchEditAttributeToUpdate>();
    const [existingAttributeFilter, setExistingAttributeFilter] =
        useState<EventAPI.BatchEditFilterAttributeName>(null);
    const [existingValueFilter, setExistingValueFilter] = useState<string>(null);

    // New Attribute Value states
    const [newAttributeSeason, setNewAttributeSeason] = useState<string>(null);
    const [newAttributeCrop, setNewAttributeCrop] = useState<string>(null);
    const [newAttributeCropPurpose, setNewAttributeCropPurpose] = useState<string>(null);
    const [newAttributeBrandOrganization, setNewAttributeBrandOrganization] =
        useState<string>(null);
    const [newAttributeVarietyHybrid, setNewAttributeVarietyHybrid] = useState<string>(null);
    const [newProductType, setNewProductType] = useState<EventAPI.BatchEditProductType>(null);
    const [newProductGuid, setNewProductGuid] = useState<string>(null);

    // State for product mixes
    const [allProducts, setAllProducts] = useState<AgEventAPI.IAvailableProduct[]>([]);
    const [manureProducts, setManureProducts] = useState([]);
    const [chemicalProducts, setChemicalProducts] = useState([]);
    const [fertilizerProducts, setFertilizerProducts] = useState([]);

    const [previewUpdateModalOpen, setPreviewUpdateModalOpen] = useState(false);
    const [updateRequestBody, setUpdateRequestBody] = useState<EventAPI.BatchEditRequest>(null);

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        dispatch(Ai360API.util.invalidateTags(["BatchEditEvents"]));
    }, [selectedEventGuidSet, dispatch]);

    const [
        eligibleEvents,
        eventType,
        validAttributesToUpdate,
        errorMessages,
        existingAttributeOptions,
    ] = useMemo(() => {
        const events: typeof agEventSummaryMap extends Map<any, infer V> ? V[] : any[] = [];
        const types = new Set<string>();
        selectedEventGuidSet.forEach((guid) => {
            const selectedEvent = agEventSummaryMap.get(guid);
            // In the case of imported events with a manual event (Planting + Application) added, the event type starts with the imported event type.
            const agEventType = selectedEvent.agEventTypeName.split(" + ")[0];
            if (
                selectedEvent &&
                [
                    models.EVENT_TYPE_NAME_APPLICATION,
                    models.EVENT_TYPE_NAME_HARVEST,
                    models.EVENT_TYPE_NAME_PLANTING,
                ].includes(agEventType) &&
                selectedEvent.isImportedYn
            ) {
                events.push(selectedEvent);
                types.add(agEventType);
            }
        });
        const attributes: EventAPI.BatchEditAttributeToUpdate[] = [
            EventAPI.BatchEditAttributeToUpdate.Season,
        ];
        const existingAttributeOptions: EventAPI.BatchEditFilterAttributeName[] = [];
        switch (Array.from(types)[0]) {
            case models.EVENT_TYPE_NAME_APPLICATION:
                attributes.push(EventAPI.BatchEditAttributeToUpdate.ProductMix);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.MonitorProduct);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.Product);
                break;
            case models.EVENT_TYPE_NAME_HARVEST:
                attributes.push(EventAPI.BatchEditAttributeToUpdate.Crop);
                attributes.push(EventAPI.BatchEditAttributeToUpdate.VarietyHybrid);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.MonitorCrop);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.MonitorVariety);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.VarietyHybrid);
                break;
            case models.EVENT_TYPE_NAME_PLANTING:
                attributes.push(EventAPI.BatchEditAttributeToUpdate.VarietyHybrid);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.MonitorCrop);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.MonitorVariety);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.VarietyHybrid);
                break;
        }
        const attributeOptions = attributes.map((v) => ({
            value: v,
            label: AttributeToUpdateMap[v],
        }));
        const errorMessages: string[] = [];
        if (events.length > MAX_BATCH_EDIT_EVENTS) {
            errorMessages.push(
                formatMessage(messages.batchEditEventsSelectionLimitWarning, {
                    limit: MAX_BATCH_EDIT_EVENTS,
                })
            );
        }
        if (types.size > 1) {
            errorMessages.push(formatMessage(messages.batchEditEventsSameEventTypeWarning));
        }
        return [
            events,
            Array.from(types)[0],
            attributeOptions,
            errorMessages,
            existingAttributeOptions,
        ];
    }, [selectedEventGuidSet, agEventSummaryMap, dispatch]);

    const { isFetching: isLinkedProductRestrictionLoading, linkedProductGuids } =
        useLinkedProductRestriction({
            isEnabled: eventType === models.EVENT_TYPE_NAME_APPLICATION,
        });

    const isUnlinkedProduct = useCallback(
        (productType: EventAPI.BatchEditProductType, productGuid: string): boolean =>
            productType === EventAPI.BatchEditProductType.SingleProduct &&
            isProductUnlinked(productGuid, linkedProductGuids),
        [linkedProductGuids]
    );

    const isSelectedProductUnlinked = isUnlinkedProduct(newProductType, newProductGuid);

    const productNotLinkedText = isSelectedProductUnlinked
        ? formatMessage(globalMessages.productNotLinkedInAgvance)
        : undefined;

    const isRequestProductUnlinked = useCallback(
        (request: EventAPI.BatchEditRequest | null): boolean =>
            Boolean(request && isUnlinkedProduct(request.productType, request.productGuid)),
        [isUnlinkedProduct]
    );

    useEffect(() => {
        if (previewUpdateModalOpen && isRequestProductUnlinked(updateRequestBody)) {
            setUpdateRequestBody(null);
            setPreviewUpdateModalOpen(false);
        }
    }, [previewUpdateModalOpen, updateRequestBody, isRequestProductUnlinked]);

    const { data: filterData, isLoading: filterDataIsLoading } =
        EventAPI.useGetBatchEditFilterValuesQuery(
            {
                eventGeneral: eligibleEvents.map((event) => event.agEventGeneralGuid),
                attributeName: existingAttributeFilter,
            },
            {
                skip: !existingAttributeFilter || selectedEventGuidSet.size === 0,
            }
        );

    const [
        filteredEventsTrigger,
        {
            data: filteredEventsData,
            isLoading: filteredEventsIsLoading,
            isFetching: filteredEventsIsFetching,
        },
    ] = EventAPI.useLazyGetBatchEditFilteredEventsQuery();

    const [batchEditUpdate, { isLoading: batchEditUpdateIsLoading }] =
        EventAPI.useBatchEditEventsMutation();

    const {
        data: brandOrgData = { model: [] },
        isLoading: brandOrgDataIsLoading,
        isFetching: brandOrgDataIsFetching,
    } = AgBytesAPI.useGetBrandOrganizationCropListQuery(newAttributeCrop, {
        skip: !newAttributeCrop,
    });

    const getVarietyHybridQueryArgs = useMemo(() => {
        return {
            CropID: newAttributeCrop,
            BrandOrganization: "",
        };
    }, [newAttributeCrop]);

    const {
        data: varietyHybridData = { model: [] },
        isLoading: varietyHybridDataIsLoading,
        isFetching: varietyHybridDataIsFetching,
    } = AgBytesAPI.useGetVarietyHybridFilterListQuery(getVarietyHybridQueryArgs, {
        skip: !newAttributeCrop,
    });

    const varietyHybridOptions = useMemo(() => {
        const options = newAttributeBrandOrganization
            ? varietyHybridData.model
                  .filter((v) => v.parentGuid === newAttributeBrandOrganization)
                  .map(({ guid, name }) => ({
                      value: guid,
                      label: name,
                  }))
            : varietyHybridData.model.map(({ guid, name }) => ({
                  value: guid,
                  label: name,
              }));
        return sortBy(options, (x) => x.label);
    }, [varietyHybridData.model, newAttributeBrandOrganization]);

    const croppingSeasonRequestMade = useRef(false);
    const cropPurposeRequestMade = useRef(false);

    useEffect(() => {
        if ((!seasons || seasons.length === 0) && !croppingSeasonRequestMade.current) {
            croppingSeasonRequestMade.current = true;
            dispatch(
                picklistActions.fetchPicklistData({
                    [PICKLIST_CROPPING_SEASON]: getPickListCode(PICKLIST_CROPPING_SEASON),
                })
            );
        }
    }, [seasons, dispatch]);

    useEffect(() => {
        if ((!cropPurposeData || cropPurposeData.length === 0) && !cropPurposeRequestMade.current) {
            cropPurposeRequestMade.current = true;
            dispatch(
                picklistActions.fetchPicklistData({
                    [PICKLIST_CROP_PURPOSE]: getPickListCode(PICKLIST_CROP_PURPOSE),
                })
            );
        }
    }, [cropPurposeData, dispatch]);

    useEffect(() => {
        dispatch(
            fetchDropdownData({
                action: fetchedDropdownData,
                cropData: {
                    url: CropAPI.REQUEST_CROP,
                    model: keywords.name,
                },
            })
        );
        const fetchProductData = async () => {
            const getChemicalAndFertilizerProducts = async (
                typesResponse: RecAPI.IGetProductTypesResponse
            ) => {
                const chemicalProductMixType = typesResponse.productMixTypes.find(
                    (type) => type.name === "Chemical"
                );
                const fertilizerProductMixType = typesResponse.productMixTypes.find(
                    (type) => type.name === "Fertilizer"
                );
                return await Promise.all([
                    chemicalProductMixType
                        ? RecAPI.getProductBlends(
                              userGuid,
                              chemicalProductMixType.productMixTypeGuid
                          )
                        : [],
                    fertilizerProductMixType
                        ? RecAPI.getProductBlends(
                              userGuid,
                              fertilizerProductMixType.productMixTypeGuid
                          )
                        : [],
                ]);
            };
            const promises = [
                RecAPI.getAvailableProducts(userGuid, {}),
                RecAPI.getCustomProducts(userGuid, {
                    customProductGuid: "",
                    physicalStateGuid: "",
                    customProductTypeGuid: "",
                    eventRecCustomProductGuids: [],
                }),
                RecAPI.getProductMixTypes(userGuid).then((typesResponse) =>
                    getChemicalAndFertilizerProducts(typesResponse)
                ),
            ];
            setIsLoading(true);
            const [allProductMixes, manureProductMixes, [chemicalMixes, fertilizerBlends]] =
                await Promise.all(promises);

            setAllProducts(allProductMixes as AgEventAPI.IAvailableProduct[]);
            setManureProducts(manureProductMixes.sort(blending.sortCustomProducts));
            setChemicalProducts(chemicalMixes);
            setFertilizerProducts(fertilizerBlends);

            setIsLoading(false);
        };
        if (eventType === models.EVENT_TYPE_NAME_APPLICATION) {
            fetchProductData();
        }
    }, [dispatch, eventType, userGuid]);

    if (errorMessages.length > 0) {
        return (
            <DialogBox
                footerType={DialogBoxFooterType.NONE}
                isOpen={true}
                onClose={closeModal}
                title={formatMessage(messages.batchEditEventsWarningTitle)}
            >
                {errorMessages.map((message, index) => (
                    <div key={index}>{message}</div>
                ))}
            </DialogBox>
        );
    }

    const onPreviewUpdate = async () => {
        logFirebaseEvent("batch_edit_events");
        const updatedAttributes: EventAPI.BatchEditRequest = {
            eventGeneral: eligibleEvents.map((event) => event.agEventGeneralGuid),
            attributeToUpdate,
            croppingSeasonGuid: newAttributeSeason,
            cropGuid: newAttributeCrop,
            cropPurposeGuid: newAttributeCropPurpose,
            brandOrganizationGuid: newAttributeBrandOrganization,
            varietyHybridGuid: newAttributeVarietyHybrid,
            productType: newProductType,
            productGuid: newProductGuid,
        };

        if (existingAttributeFilter) {
            const filterDataItem = filterData.find(
                (item) => item.id === existingValueFilter || item.value === existingValueFilter
            );
            if (filterDataItem) {
                updatedAttributes.filterAttributeName = existingAttributeFilter;
                updatedAttributes.filterValue = filterDataItem.value;
                updatedAttributes.filterGuid = filterDataItem.id;
            }
        }

        setUpdateRequestBody(updatedAttributes);
        setPreviewUpdateModalOpen(true);
        filteredEventsTrigger(updatedAttributes);
    };

    const onCancelUpdate = async () => {
        setUpdateRequestBody(null);
        setPreviewUpdateModalOpen(false);
    };

    const onUpdate = async () => {
        if (isRequestProductUnlinked(updateRequestBody)) {
            setUpdateRequestBody(null);
            setPreviewUpdateModalOpen(false);
            return;
        }
        setIsLoading(true);
        setUpdateRequestBody(null);
        setPreviewUpdateModalOpen(false);
        await batchEditUpdate(updateRequestBody)
            .unwrap()
            .then(() => {
                setNewAttributeSeason(null);
                setNewAttributeCrop(null);
                setNewAttributeCropPurpose(null);
                setNewAttributeBrandOrganization(null);
                setNewAttributeVarietyHybrid(null);
                setNewProductType(null);
                setNewProductGuid(null);
                dispatch(recEventsActions.refreshFilterOptions());
                dispatch(
                    batchEditEventsSave(
                        eligibleEvents.map((e) => ({
                            fieldGuid: e.fieldGuid,
                            agEventGeneralGuid: e.agEventGeneralGuid,
                        })),
                        attributeToUpdate
                    )
                );
            })
            .catch(() => {
                // Handled by WithNewApiResult
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const renderExistingAttributeFilter = () => {
        if (
            !attributeToUpdate ||
            attributeToUpdate === EventAPI.BatchEditAttributeToUpdate.Season
        ) {
            return null;
        }
        return (
            <div className="batch-edit-events-group">
                <div className="batch-edit-events-group-header">
                    {formatMessage(messages.batchEditEventsDataFilters)}
                </div>
                <SelectInput
                    placeholderText={formatMessage(messages.batchEditEventsExistingAttributeFilter)}
                    options={existingAttributeOptions.map((s) => ({
                        value: s,
                        label: FilterAttributeMap[s],
                    }))}
                    value={existingAttributeFilter}
                    onChange={(value) => {
                        setExistingAttributeFilter(value);
                        setExistingValueFilter(null);
                    }}
                />
                <SelectInput
                    placeholderText={formatMessage(messages.batchEditEventsExistingValueFilter)}
                    // Options for this should be based on monitor names from the selected events
                    options={sortBy(
                        (filterData || []).map((s) => ({
                            value: s.id || s.value,
                            label: s.value,
                        })),
                        (x) => x.label
                    )}
                    value={existingValueFilter}
                    onChange={(value) => setExistingValueFilter(value)}
                    disabled={!existingAttributeFilter}
                />
            </div>
        );
    };

    const renderProductMixDropdown = () => {
        if (!newProductType) {
            return null;
        }
        const options = [];
        let placeholder = "";
        switch (newProductType) {
            case EventAPI.BatchEditProductType.SingleProduct:
                filterLinkedProducts(
                    allProducts,
                    linkedProductGuids,
                    (product) => product.productGuid,
                    newProductGuid ? [newProductGuid] : []
                ).forEach((product) => {
                    options.push({
                        value: product.productGuid,
                        label: product.name,
                    });
                });
                placeholder = formatMessage(messages.batchEditEventsProductMixSingleProduct);
                break;
            case EventAPI.BatchEditProductType.ManureProduct:
                manureProducts.forEach((product) => {
                    options.push({
                        value: product.customProductGuid,
                        label: `${product.customProductType} - ${product.guaranteedAnalysis} (${product.physicalState})`,
                    });
                });
                placeholder = formatMessage(messages.batchEditEventsProductMixManureProduct);
                break;
            case EventAPI.BatchEditProductType.CustomBlend:
                fertilizerProducts.forEach((product) => {
                    options.push({
                        value: product.productBlendGuid,
                        label: product.name,
                    });
                });
                placeholder = formatMessage(messages.batchEditEventsProductMixCustomBlend);
                break;
            case EventAPI.BatchEditProductType.ChemicalProductMix:
                chemicalProducts.forEach((product) => {
                    options.push({
                        value: product.productBlendGuid,
                        label: product.name,
                    });
                });
                placeholder = formatMessage(messages.batchEditEventsProductMixChemicalProductMix);
                break;
        }
        return (
            <SelectInput
                key={EventAPI.BatchEditAttributeToUpdate.ProductMix + "1"}
                clearable={false}
                hasError={isSelectedProductUnlinked}
                placeholderText={placeholder}
                options={options}
                toolTip={productNotLinkedText}
                value={newProductGuid}
                onChange={(value) => setNewProductGuid(value)}
                disabled={!newProductType}
            />
        );
    };

    const renderNewAttributeValues = () => {
        const selectInputs = [];
        switch (attributeToUpdate) {
            case EventAPI.BatchEditAttributeToUpdate.Season:
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.Season}
                        placeholderText={formatMessage(messages.batchEditEventsSeason)}
                        options={seasons}
                        value={newAttributeSeason}
                        clearable={false}
                        required
                        onChange={(value) => setNewAttributeSeason(value)}
                    />
                );
                break;
            case EventAPI.BatchEditAttributeToUpdate.Crop:
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.Crop}
                        placeholderText={formatMessage(messages.batchEditEventsCrop)}
                        options={sortBy(
                            cropData.map(({ guid, name }) => ({
                                value: guid,
                                label: name,
                            })),
                            (x) => x.label
                        )}
                        value={newAttributeCrop}
                        clearable={false}
                        onChange={(value) => {
                            setNewAttributeBrandOrganization(null);
                            setNewAttributeVarietyHybrid(null);
                            setNewAttributeCropPurpose(null);
                            setNewAttributeCrop(value);
                        }}
                        required
                    />
                );
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.Crop + "1"}
                        placeholderText={formatMessage(messages.batchEditEventsCropPurpose)}
                        options={sortBy(cropPurposeData, (x) => x.label)}
                        value={newAttributeCropPurpose}
                        clearable={true}
                        onChange={setNewAttributeCropPurpose}
                    />
                );
                break;
            case EventAPI.BatchEditAttributeToUpdate.VarietyHybrid:
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.VarietyHybrid}
                        placeholderText={formatMessage(messages.batchEditEventsCrop)}
                        options={sortBy(
                            cropData.map(({ guid, name }) => ({
                                value: guid,
                                label: name,
                            })),
                            (x) => x.label
                        )}
                        value={newAttributeCrop}
                        clearable={false}
                        onChange={(value) => {
                            setNewAttributeBrandOrganization(null);
                            setNewAttributeVarietyHybrid(null);
                            setNewAttributeCrop(value);
                        }}
                        required
                    />
                );
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.VarietyHybrid + "1"}
                        placeholderText={formatMessage(messages.batchEditEventsCropPurpose)}
                        options={sortBy(cropPurposeData, (x) => x.label)}
                        value={newAttributeCropPurpose}
                        clearable={true}
                        onChange={setNewAttributeCropPurpose}
                    />
                );
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.VarietyHybrid + "2"}
                        placeholderText={formatMessage(messages.batchEditEventsBrandOrganization)}
                        options={sortBy(
                            brandOrgData.model.map(({ guid, name }) => ({
                                value: guid,
                                label: name,
                            })),
                            (x) => x.label
                        )}
                        value={newAttributeBrandOrganization}
                        onChange={(value) => {
                            if (newAttributeVarietyHybrid) {
                                const variety = varietyHybridData.model.find(
                                    (v) =>
                                        v.parentGuid === value &&
                                        v.guid === newAttributeVarietyHybrid
                                );
                                if (!variety) {
                                    setNewAttributeVarietyHybrid(null);
                                }
                            }
                            setNewAttributeBrandOrganization(value);
                        }}
                        disabled={!newAttributeCrop}
                        clearable={eventType !== models.EVENT_TYPE_NAME_PLANTING}
                        required={eventType === models.EVENT_TYPE_NAME_PLANTING}
                    />
                );
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.VarietyHybrid + "3"}
                        placeholderText={formatMessage(messages.batchEditEventsVarietyHybrid)}
                        options={varietyHybridOptions}
                        value={newAttributeVarietyHybrid}
                        onChange={(value) => {
                            setNewAttributeVarietyHybrid(value);
                            if (value) {
                                const newBrandOrgValue =
                                    varietyHybridData.model.find((v) => v.guid === value)
                                        ?.parentGuid || null;
                                if (newAttributeBrandOrganization !== newBrandOrgValue) {
                                    setNewAttributeBrandOrganization(newBrandOrgValue);
                                }
                            }
                        }}
                        disabled={!newAttributeCrop}
                        clearable={eventType !== models.EVENT_TYPE_NAME_PLANTING}
                        required={eventType === models.EVENT_TYPE_NAME_PLANTING}
                    />
                );
                break;
            case EventAPI.BatchEditAttributeToUpdate.ProductMix:
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.ProductMix}
                        placeholderText={formatMessage(messages.batchEditEventsProductMixType)}
                        options={ProductMixTypeOptions}
                        value={newProductType}
                        clearable={false}
                        onChange={(value) => {
                            setNewProductType(value);
                            setNewProductGuid(null);
                        }}
                    />
                );
                selectInputs.push(renderProductMixDropdown());
                break;
        }
        if (selectInputs.length === 0) {
            return null;
        }
        return (
            <div className="batch-edit-events-group">
                <div className="batch-edit-events-group-header">
                    {formatMessage(messages.batchEditEventsNewAttributeValues)}
                </div>
                {selectInputs}
            </div>
        );
    };

    let onUpdateEnabled = false;
    switch (attributeToUpdate) {
        case EventAPI.BatchEditAttributeToUpdate.Season:
            onUpdateEnabled = !!newAttributeSeason;
            break;
        case EventAPI.BatchEditAttributeToUpdate.Crop:
            onUpdateEnabled = !!newAttributeCrop;
            break;
        case EventAPI.BatchEditAttributeToUpdate.VarietyHybrid:
            if (eventType === models.EVENT_TYPE_NAME_PLANTING) {
                onUpdateEnabled = !!newAttributeVarietyHybrid;
            } else {
                onUpdateEnabled = !!newAttributeCrop;
            }
            break;
        case EventAPI.BatchEditAttributeToUpdate.ProductMix:
            onUpdateEnabled = !!newProductType && !!newProductGuid && !isSelectedProductUnlinked;
            break;
    }

    const showLoader =
        isLoading ||
        filterDataIsLoading ||
        filteredEventsIsLoading ||
        batchEditUpdateIsLoading ||
        brandOrgDataIsFetching ||
        brandOrgDataIsLoading ||
        varietyHybridDataIsFetching ||
        varietyHybridDataIsLoading ||
        isLinkedProductRestrictionLoading;

    return (
        <>
            <DialogBox
                action={formatMessage(messages.batchEditEventsUpdate)}
                actionDisabled={!onUpdateEnabled}
                draggable={true}
                unrestricted
                footerType={DialogBoxFooterType.ACTION_CLOSE}
                isOpen={true}
                onAction={onPreviewUpdate}
                onClose={closeModal}
                title={formatMessage(messages.batchEditEventsTitle, {
                    count: eligibleEvents.length,
                })}
                className="batch-edit-events-modal"
            >
                <div className="batch-edit-form">
                    <div className="batch-edit-events-group">
                        <SelectInput
                            clearable={false}
                            placeholderText={formatMessage(
                                messages.batchEditEventsAttributeToUpdate
                            )}
                            options={validAttributesToUpdate}
                            value={attributeToUpdate}
                            onChange={(value: EventAPI.BatchEditAttributeToUpdate) =>
                                setAttributeToUpdate(value)
                            }
                        />
                    </div>
                    {renderExistingAttributeFilter()}
                    {renderNewAttributeValues()}
                </div>
                {showLoader && <Loader className="batch-edit-events-loader" />}
            </DialogBox>
            <DialogBox
                isOpen={
                    previewUpdateModalOpen &&
                    !filteredEventsIsFetching &&
                    !!filteredEventsData &&
                    !isRequestProductUnlinked(updateRequestBody)
                }
                isModal={true}
                title={formatMessage(messages.batchEditEventsConfirmTitle)}
                draggable={true}
                hideCloseX={true}
                onAction={onUpdate}
                onClose={onCancelUpdate}
                footerType={DialogBoxFooterType.YES_NO}
            >
                {formatMessage(messages.batchEditEventsConfirmText, {
                    count: filteredEventsData?.length,
                })}
            </DialogBox>
        </>
    );
};

BatchEditEventsModal.propTypes = {
    intl: intlShape,
};

export default WithNewApiResult(injectIntl(BatchEditEventsModal));
